import { FLOW_ID_SESSION_KEY } from '@tn/foundation/utils/constants/common';

import { EMAIL_STORAGE_KEY } from './constants';

const EMAIL_CHECKPOINT_FLOW_IDS = ['ptvSisu', 'activateDevice'] as const;

type EmailCheckpointFlowId = (typeof EMAIL_CHECKPOINT_FLOW_IDS)[number];

const isEmailCheckpointFlowId = (flowId: string | null): flowId is EmailCheckpointFlowId =>
    flowId !== null && EMAIL_CHECKPOINT_FLOW_IDS.some((id) => id === flowId);

const SISU_EMAIL_FLOW_PATH_SEGMENTS = [
    '/account/sign-in',
    '/account/sign-up',
    '/account/check-email',
] as const;

const ACTIVATE_DEVICE_FLOW_PATH_SEGMENTS = ['/activate/device', '/account/confirm'] as const;

const pathIncludesAnySegment = (url: string, segments: readonly string[]): boolean => {
    const path = url.split('?')[0].split('#')[0];
    return segments.some((segment) => path.includes(segment));
};

/** True when `url` stays within the email-checkpoint auth steps (region-aware). */
export const isStayingInSisuEmailFlow = (url: string): boolean => {
    return pathIncludesAnySegment(url, SISU_EMAIL_FLOW_PATH_SEGMENTS);
};

/** True when `url` stays within the active email-checkpoint flow (flow-aware). */
export const isStayingInEmailCheckpointFlow = (
    url: string,
    flowId: EmailCheckpointFlowId,
): boolean => {
    if (isStayingInSisuEmailFlow(url)) {
        return true;
    }
    if (flowId === 'activateDevice') {
        return pathIncludesAnySegment(url, ACTIVATE_DEVICE_FLOW_PATH_SEGMENTS);
    }
    return false;
};

// the email captured at /account/check-email is stashed in
// sessionStorage (not a cookie or the URL) so a fresh render of /account/sign-{in,up} or a hard
// refresh can rehydrate `context.email`. sessionStorage is per-tab, so a concurrent submission in
// another tab can't overwrite this tab's value — and because the value never travels
// in a cookie it stays out of request headers and server logs. Shared by the ptvSisu
// and activateDevice flows (writers) and the PTV appshell client guard (reader).

export function persistEmailToSession(value: string | null | undefined): void {
    if (typeof window === 'undefined') return;
    try {
        if (typeof value === 'string' && value.length > 0) {
            window.sessionStorage.setItem(EMAIL_STORAGE_KEY, value);
        }
    } catch {
        // sessionStorage may throw in private mode or when the quota is exceeded; swallow.
    }
}

export function readEmailFromSession(): string | null {
    if (typeof window === 'undefined') return null;
    try {
        return window.sessionStorage.getItem(EMAIL_STORAGE_KEY);
    } catch {
        return null;
    }
}

export function clearEmailFromSession(): void {
    if (typeof window === 'undefined') return;
    try {
        window.sessionStorage.removeItem(EMAIL_STORAGE_KEY);
    } catch {
        // Same rationale as above.
    }
}

/**
 * Clears the tab-scoped email checkpoint when browser navigation leaves the
 * active flow. Invoked from `nativeBrowserNavigation` on `routeChangeStart` /
 * `popstate` while `ptvSisu` or `activateDevice` is active — in-flow hops are
 * left untouched (`ptvSisu`: auth steps only; `activateDevice`: auth steps plus
 * `/activate/device` and `/account/confirm`).
 *
 * In-flow CHECK_EMAIL events are cleared by ptvSisu.clearEmailCheckpoint.
 */
export function clearEmailCheckpointOnNavigation(url: string): void {
    if (typeof window === 'undefined') return;
    try {
        const flowId = window.sessionStorage.getItem(FLOW_ID_SESSION_KEY);
        if (!isEmailCheckpointFlowId(flowId)) {
            return;
        }
        if (!isStayingInEmailCheckpointFlow(url, flowId)) {
            clearEmailFromSession();
        }
    } catch {
        // Same rationale as above.
    }
}
